import { useEffect, useMemo, useRef, useState } from "react";
import {
  GenerativeDenimTile,
  getGenerativeAssetPreview,
} from "./generativeCatalog";

/** Blob-fetch denim material/wash tiles through the authenticated asset proxy. */
export const useGenerativeDenimPreviews = (
  tiles: GenerativeDenimTile[],
  enabled = true
) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const cacheRef = useRef(new Map<string, string>());
  const tilesRef = useRef(tiles);
  tilesRef.current = tiles;
  const signature = useMemo(
    () =>
      tiles
        .map(item => `${item.id}:${item.url}`)
        .sort()
        .join("|"),
    [tiles]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    const currentTiles = tilesRef.current;
    (async () => {
      const missing = currentTiles.filter(
        item => item.url && !cacheRef.current.has(item.id)
      );
      if (!missing.length) {
        if (!cancelled) {
          setPreviews(Object.fromEntries(cacheRef.current));
        }
        return;
      }
      await Promise.all(
        missing.map(async item => {
          try {
            const previewUrl = await getGenerativeAssetPreview(item.url);
            cacheRef.current.set(item.id, previewUrl);
          } catch {
            // Keep the tile as a placeholder if the asset cannot be fetched.
          }
        })
      );
      if (!cancelled) {
        setPreviews(Object.fromEntries(cacheRef.current));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, signature]);

  useEffect(
    () => () => {
      cacheRef.current.forEach(url => URL.revokeObjectURL(url));
      cacheRef.current.clear();
    },
    []
  );

  return previews;
};
